import { useEffect, useState, useCallback } from "react";
import { FileText } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { Spinner } from "@/components/ui/spinner";
import { PdfFrame } from "@/components/PdfFrame";

export interface BillDocumentViewerProps {
  billId: string;
}

export function BillDocumentViewer({ billId }: BillDocumentViewerProps) {
  const api = useDolfinClient();
  const [fileData, setFileData] = useState<{ url: string; fileType: string | null | undefined } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFileUrl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, response } = await api.GET("/v1/bills/{id}/file-url", {
        params: { path: { id: billId } },
      });
      if (response.status === 404) {
        setError("No document available");
        return;
      }
      if (!data) {
        setError("Failed to load document");
        return;
      }
      setFileData({ url: data.url, fileType: data.fileType });
    } catch {
      setError("Failed to load document");
    } finally {
      setLoading(false);
    }
  }, [billId, api]);

  useEffect(() => {
    fetchFileUrl();
  }, [fetchFileUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="size-6 text-muted-foreground" />
      </div>
    );
  }

  if (error || !fileData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <FileText className="size-10" />
        <p className="text-sm">{error || "No document available"}</p>
      </div>
    );
  }

  const isImage = fileData.fileType?.startsWith("image/");

  if (isImage) {
    return (
      <div className="h-full overflow-auto p-4">
        <img src={fileData.url} alt="Bill document" className="max-w-full h-auto rounded-md" />
      </div>
    );
  }

  return <PdfFrame src={fileData.url} title="Bill document" className="w-full h-full border-0" />;
}
